import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bot, Copy, Sparkles } from "lucide-react";

const TASKS = {
  reply: { title: "Draft a client reply", placeholder: "Paste the client's message or context…" },
  summarize: { title: "Summarize appointment notes", placeholder: "Paste raw grooming notes for the appointment…" },
  reminder: { title: "Compose an SMS reminder", placeholder: "Example: 'Biscuit — Full Groom, Fri 10:30 AM'" },
};

export default function AIAssistant() {
  const [task, setTask] = useState("reply");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!input.trim()) { toast.error("Add some context first"); return; }
    setBusy(true); setOutput("");
    try {
      const { data } = await api.post("/ai", { task, text: input });
      setOutput(data.text);
    } catch (e) { toast.error(e.response?.data?.detail || "AI request failed"); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    toast.success("Copied");
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6">
      <header>
        <div className="label-accent flex items-center gap-1"><Sparkles className="w-3 h-3"/> Co-pilot</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">AI assistant</h1>
        <p className="text-sm text-[#6B625B] mt-2">Powered by Claude Sonnet 5 · human review required before sending.</p>
      </header>

      <Tabs value={task} onValueChange={(v) => { setTask(v); setOutput(""); }}>
        <TabsList className="bg-[#F3EDE4]">
          <TabsTrigger value="reply" data-testid="tab-reply">Reply draft</TabsTrigger>
          <TabsTrigger value="summarize" data-testid="tab-summarize">Notes summary</TabsTrigger>
          <TabsTrigger value="reminder" data-testid="tab-reminder">SMS reminder</TabsTrigger>
        </TabsList>
        {Object.entries(TASKS).map(([k, v]) => (
          <TabsContent key={k} value={k}>
            <div className="bg-white rounded-3xl border border-[#E5DCCE] p-6 shadow-sm space-y-4">
              <h3 className="font-display text-lg font-bold">{v.title}</h3>
              <Textarea rows={6} value={input} onChange={(e) => setInput(e.target.value)}
                placeholder={v.placeholder} data-testid="ai-input"/>
              <Button onClick={run} disabled={busy}
                className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile" data-testid="btn-ai-run">
                {busy ? <Sparkles className="w-4 h-4 mr-2 animate-pulse"/> : <Bot className="w-4 h-4 mr-2"/>}
                {busy ? "Thinking…" : "Generate"}
              </Button>
              {output && (
                <div className="bg-[#F5E6CF] border border-amber-900/10 rounded-2xl p-4 relative">
                  <button onClick={copy} className="absolute top-3 right-3 text-[#A9480F] hover:text-[#7A3A09]"
                    data-testid="btn-copy">
                    <Copy className="w-4 h-4"/>
                  </button>
                  <div className="text-sm whitespace-pre-wrap pr-8">{output}</div>
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
